<template>
  <div>
    <svg
      :viewBox="eye.viewBox"
      :width="eye.width * 1"
      :height="eye.height"
      ref="svg"
      @mousemove="drag"
      @mouseup="endDrag"
      @mouseleave="endDrag"
    >
      <defs>
        <radialGradient id="rg" cx="50%" cy="50%" r="50%">
          <stop stop-color="white" offset="0%" />
          <stop stop-color="#daeaf0" offset="98%" stop-opacity="0.8" />
        </radialGradient>
        <linearGradient id="hback" x2="0" y2="100%">
          <stop offset="0%" stop-color="#DEEDF2" />
          <stop offset="100%" stop-color="#FCFDFE" />
        </linearGradient>
      </defs>

      <path
        ref="circlePath"
        :d="dottedLineData"
        :stroke="eye.stroke"
        stroke-dasharray="6 6"
        fill="none"
      />

      <path
        class="horizontal-gradient"
        ref="lensePath"
        :d="pathData"
        :stroke="eye.stroke"
        :stroke-width="eye.strokeWidth"
        :fill="eye.bgColor"
      />

      <line
        v-if="hasEspesores"
        :x1="horizontalGuideLine.x1"
        :x2="horizontalGuideLine.x2"
        y1="0"
        y2="0"
        stroke="#58b6ec"
        stroke-dasharray="6 3 1 3"
      />

      <line
        :x1="horizontalGuideLine.x1"
        :x2="horizontalGuideLine.x2"
        :y1="cruzMontajeY"
        :y2="cruzMontajeY"
        stroke="#58b6ec"
        stroke-dasharray="6 3 1 3"
        opacity="0.7"
        v-if="isProgresivo"
      />

      <line
        v-if="hasEspesores"
        :x1="x"
        :x2="redLineX"
        y1="0"
        :y2="redLineY"
        stroke="red"
        stroke-width="2"
      />

      <line
        v-if="hasEspesores"
        :x1="x"
        :x2="greenLineX"
        y1="0"
        :y2="greenLineY"
        stroke="green"
        stroke-width="2"
      />

      <circle
        :cx="montajeXi"
        cy="0"
        r="1.8"
        stroke-width="1"
        stroke="#58b6ec"
        fill="transparent"
        v-if="isProgresivo"
      />

      <circle
        :cx="montajeXd"
        cy="0"
        r="1.8"
        stroke-width="1"
        stroke="#58b6ec"
        fill="transparent"
        v-if="isProgresivo"
      />

      <circle
        :cx="lentilla.x"
        :cy="lentilla.y"
        :r="lentilla.radio"
        stroke-width="1"
        stroke="#58b6ec"
        fill="transparent"
        v-if="isProgresivo"
      />

      <line
        :x1="cruzMontajeX"
        :x2="cruzMontajeX"
        :y1="cruzMontajeY + 2.5"
        :y2="cruzMontajeY - 2.5"
        stroke-width="1.5"
        stroke="black"
        v-if="isProgresivo"
      />
      <line
        :x1="cruzMontajeX - 2.5"
        :x2="cruzMontajeX + 2.5"
        :y1="cruzMontajeY"
        :y2="cruzMontajeY"
        stroke-width="1.5"
        stroke="black"
        v-if="isProgresivo"
      />

      <circle
        v-for="(taladro, index) in taladrosCoordenadas"
        :key="`taladro-${index}`"
        :cx="taladro.x || 0"
        :cy="taladro.y || 0"
        :r="taladro.r ?? 1"
        fill="red"
        :stroke="taladro.r !== undefined ? 'none' : 'darkred'"
        stroke-width="1"
        opacity="1"
      />

      <line
        v-if="hasEspesores"
        :x1="point.x"
        :x2="x"
        :y1="point.y"
        y2="0"
        stroke="black"
        stroke-width="2"
        ref="blackLine"
      />
      <circle
        v-if="hasEspesores"
        ref="pointer"
        :cx="point.x || 0"
        :cy="point.y || 0"
        r="2"
        stroke="2"
        fill="black"
        @mousedown="startDrag"
        cursor="pointer"
      />
    </svg>
    <div v-if="showCaption" class="text-lent">
      {{ eye.name.toUpperCase() }}: <span>{{ eye.baseText }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  reactive,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance,
  useTemplateRef,
} from "vue";
import eventBus from "@/helpers/eventBus";
import { useIndoStore } from "@/stores/indo";
import { useOfertasStore } from "@/stores/ofertas";
import { decodeFormas } from "@/helpers/formulas";

type Eye = {
  name: string;
  viewBox?: string;
  width?: number;
  height?: number;
  stroke?: string;
  strokeWidth?: number;
  bgColor?: string;
  FormaRO?: string;
  FormaSO?: string;
  FormaEO?: string;
  DIA_RE?: string;
  desplazamiento?: number;
  angulo?: number;
  ESPESOR?: number;
  ESP_MIN?: number;
  ESP_MAX?: number;
  radio?: number;
  baseText?: string;
  TAL_PUNTOS?: string;
  TAL_SECUENCIA?: string;
  puntoS_TALADROS?: string;
  desplazamientoTaladros?: { x: number; y: number };
  adicX?: string | number;
  adicY?: number;
  [key: string]: unknown;
};

type Ajustes = { VALOR_PANTALLA?: number };

const props = withDefaults(
  defineProps<{
    eye: Eye;
    numeroPuntos?: number;
    lensesType?: string;
    /** Si viene (p. ej. render off-screen PDF), evita otro LoadInfo y replica el mismo VP que el formulario */
    initialAjustes?: Ajustes | null;
    /** Si false, no se muestra la línea OD/OI debajo del SVG (p. ej. texto agrupado en el padre) */
    showCaption?: boolean;
  }>(),
  { numeroPuntos: 600, initialAjustes: null, showCaption: true },
);

const indoStore = useIndoStore();
const ofertasStore = useOfertasStore();

const svg = useTemplateRef<SVGSVGElement>("svg");
const lensePath = useTemplateRef<SVGPathElement>("lensePath");
useTemplateRef("circlePath");
useTemplateRef("pointer");
useTemplateRef("blackLine");

const escala = ref(1.35);
const ajustes = ref<Ajustes>({});
const dragging = ref(false);
const precision = 100;
const point = reactive({ x: 0, y: 0 });
const diametro = ref<string | number>(0);
const espesores = ref<number[]>([]);
const longitudArrays = ref(0);
const r = ref(0);
const blankHalfWidth = ref(0);
const redLineX = ref(0);
const redLineY = ref(0);
const greenLineX = ref(0);
const greenLineY = ref(0);
const forma = ref<number[]>([]);
const x = ref(0);
const montajeXi = ref(0);
const montajeXd = ref(0);
const lentilla = reactive({ x: 0, y: 0, radio: 0 });
const caracteristicasLocal = ref<Record<string, unknown> | null>(null);

const instance = getCurrentInstance();

const caracteristicas = computed(() => ofertasStore.caracteristicas);
const nuevoPedido = computed(() => indoStore.pedido);
const caracteristicasDraw = computed(() => caracteristicasLocal.value ?? caracteristicas.value);

const pedidoJergaForEye = computed(() => {
  const list = nuevoPedido.value?.oClaseBAPI?.listOJOIN?.clsZES_OJO_INPUT;
  if (!Array.isArray(list)) return null;
  const zojo = props.eye?.name === "oi" ? "I" : "D";
  return (
    list.find((o: { ZOJO: string; JER_EDI_INET?: string }) => o.ZOJO === zojo)?.JER_EDI_INET || null
  );
});

/** Sin forma: ORIGEN_DATOS "" o "0" (ver FormsThickness / FormaLente) */
const isPedidoSinForma = computed(() => {
  const cab = nuevoPedido.value?.oClaseBAPI?.listCABFRM;
  if (!cab) return false;
  const frm = Array.isArray(cab) && cab[0] ? cab[0] : cab?.clsZES_CAB_FORMA?.[0];
  const o = frm?.ORIGEN_DATOS;
  return o === "" || o === "0";
});

const getCircleOffset = computed(() => 14);

/**
 * Tipos de lentilla que en el VB (Forma.vb) se dibujan como redonda/telegrand:
 * el centro baja media anchura y el radio = ancho/2 (ver calculateLentilla).
 */
const TIPOS_LENTILLA_REDONDA = new Set(["FLAT", "CURV2", "ROUND", "CURV"]);

/** Factor con que el contorno del blank (línea de puntos / FormaSO) rodea al de la lente. */
const FACTOR_DOTTED = 1.01;

/**
 * Escala mm→px del SVG. El contorno (calcularPuntosCircunferencia), la cruz de
 * montaje (cruzMontajeY) y la lentilla deben compartir esta misma escala para
 * quedar alineados dentro del mismo viewBox.
 */
const svgScale = computed(
  () => Math.pow(60 / (ajustes.value?.VALOR_PANTALLA || 60), 2) * escala.value,
);

/**
 * Regla de 3 mm→px del dibujo: la MISMA que usa el contorno
 * (calcularPuntosCircunferencia) y la cruz de montaje vertical. La bola de
 * adición debe pasar por aquí para quedar alineada con el resto del SVG.
 */
function mmToSvg(mm: number) {
  return mm * svgScale.value;
}

function mmToPx(mm: number) {
  const DPI = 96;
  const pixelRatio = 1;
  return (DPI * pixelRatio * mm) / 25.4;
}

function calculateScale(px: number) {
  if (ajustes.value.VALOR_PANTALLA) {
    return px * (ajustes.value.VALOR_PANTALLA / 100 / 2 + ajustes.value.VALOR_PANTALLA / 100);
  }
  return px;
}

function calculatedSize(px: number) {
  if (ajustes.value.VALOR_PANTALLA) {
    const size = ((px - 5) * px) / ajustes.value.VALOR_PANTALLA;
    return Math.round(mmToPx(size));
  }
  return px;
}

function calcularPuntosCircunferencia(puntos: number[], numPuntos: number, factorExtra = 1) {
  let pathD = `M `;
  const alfaIncrease = ((-360 / numPuntos) * Math.PI) / 180;
  let d = 0;

  for (let i = 0; i < numPuntos; i++) {
    const px = mmToSvg(puntos[i]) * Math.cos(d) * factorExtra;
    const py = mmToSvg(puntos[i]) * Math.sin(d) * factorExtra;
    if (i === 0) point.x = px;
    if (i > 0) pathD += "L";
    pathD += `${px},${py} `;
    d += alfaIncrease;
  }
  pathD += `Z`;
  return pathD;
}

const pathData = computed(() => {
  if (props.eye.FormaRO) {
    const final = decodeFormas(props.eye.FormaRO, precision, props.numeroPuntos);
    longitudArrays.value = final.length;
    forma.value = final;
    return calcularPuntosCircunferencia(final, props.numeroPuntos);
  }
  return "";
});

const dottedLineData = computed(() => {
  if (isPedidoSinForma.value) return "";
  if (props.eye.FormaSO && props.eye.DIA_RE) {
    const final = decodeFormas(props.eye.FormaSO, precision, props.numeroPuntos);
    longitudArrays.value = final.length;
    return calcularPuntosCircunferencia(final, props.numeroPuntos, FACTOR_DOTTED);
  }
  return "";
});

const isProgresivo = computed(() => caracteristicasDraw.value?.Tipo === "P");

const hasEspesores = computed(() => !!props.eye.FormaEO);

/** Extremos de la línea horizontal: coincide con el perímetro del blank (FormaSO), no con eye.width/2-r */
const horizontalGuideLine = computed(() => {
  const half = blankHalfWidth.value > 0 ? blankHalfWidth.value : r.value > 0 ? r.value : 0;
  if (half > 0) return { x1: -half, x2: half };
  return { x1: "-100%", x2: "100%" };
});

/** Solo respuesta GetCaracteristicasLente de este ojo (evita CruzMontaje del store de otra lente) */
const cruzMontajeX = computed(() => {
  const c = caracteristicasLocal.value;
  if (!c?.CruzMontaje) return 0;
  const cruz = String(c.CruzMontaje).split(";")[0];
  const parsed = parseFloat(String(cruz).replace(",", "."));
  if (!isNaN(parsed) && parsed != 0) {
    const result = parseFloat(String(calculateScale(mmToPx(parsed))));
    return isNaN(result) ? 0 : result;
  }
  return 0;
});

/** Y en API (mm): positivo = hacia arriba en el cristal; SVG: Y+ = abajo → invertir */
const cruzMontajeY = computed(() => {
  const c = caracteristicasLocal.value;
  if (!c?.CruzMontaje) return 0;
  const cruz = String(c.CruzMontaje).split(";")[1];
  const parsed = parseFloat(String(cruz).replace(",", "."));
  if (!isNaN(parsed) && parsed != 0 && ajustes.value?.VALOR_PANTALLA) {
    const result = parsed * svgScale.value;
    return isNaN(result) ? 0 : -result;
  }
  return 0;
});

function calcularTaladro(xCoord: number, yCoord: number) {
  const e = escala.value || 1.35;
  if (isNaN(e) || e === 0) return { x: 0, y: 0 };
  const factorEscala = 0.7;
  const realX = (xCoord / 100) * e * factorEscala;
  const realY = (yCoord / 100) * e * factorEscala;
  if (isNaN(realX) || isNaN(realY)) return { x: 0, y: 0 };
  const finalY = -realY;
  const finalX = props.eye.name === "oi" ? -realX : realX;
  return { x: finalX || 0, y: finalY || 0 };
}

const taladrosCoordenadas = computed<
  Array<{
    x: number;
    y: number;
    r?: number;
  }>
>(() => {
  // Taladros procedentes de OneTracerAgent (formato X#Y#DIAMETRO).
  const puntosTaladros = props.eye.puntoS_TALADROS;

  if (typeof puntosTaladros === "string") {
    return puntosTaladros.split("//").flatMap((taladro) => {
      const valores = taladro.split("#");

      if (valores.length !== 3) return [];

      const x = Number(valores[0].replace(",", "."));
      const y = Number(valores[1].replace(",", "."));
      const diametro = Number(valores[2].replace(",", "."));

      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(diametro) || diametro <= 0)
        return [];

      // Aplicar el descentramiento de SAP únicamente al dibujo.
      // Las coordenadas originales del trazador permanecen intactas.
      // El desplazamiento Y está en milímetros, con el signo del eje SVG
      // (positivo hacia abajo). Se convierte a unidades SVG al dibujar.
      const desplazamiento = props.eye.desplazamientoTaladros ?? { x: 0, y: 0 };

      return [
        {
          x: mmToSvg(x + desplazamiento.x),
          y: -mmToSvg(y) + mmToSvg(desplazamiento.y),
          r: mmToSvg(diametro / 2),
        },
      ];
    });
  }

  const parentInstance = instance?.parent;
  const parentOjos = (parentInstance?.exposed as { ojos?: Eye[] } | undefined)?.ojos;
  const ojoDerecho = parentOjos?.find((ojo) => ojo.name === "od") || props.eye;

  if (!ojoDerecho.TAL_PUNTOS || !ojoDerecho.TAL_SECUENCIA) return [];

  let puntos = ojoDerecho.TAL_PUNTOS;
  let secuencia = ojoDerecho.TAL_SECUENCIA;
  if (puntos.endsWith("#")) puntos = puntos.slice(0, -1);
  if (secuencia.endsWith("#")) secuencia = secuencia.slice(0, -1);

  const arraySecuencia = secuencia.split("#");
  const arrayPuntos = puntos.split("#");
  const coordenadas: Array<{ x: number; y: number }> = [];

  let n = 0;
  for (let i = 0; i < arraySecuencia.length; i++) {
    if (arraySecuencia[i] === "1") {
      const xv = parseInt(arrayPuntos[n]);
      const yv = parseInt(arrayPuntos[n + 1]);
      if (!isNaN(xv) && !isNaN(yv)) coordenadas.push(calcularTaladro(xv, yv));
      n += 2;
    } else {
      const x1 = parseInt(arrayPuntos[n]);
      const y1 = parseInt(arrayPuntos[n + 1]);
      const x2 = parseInt(arrayPuntos[n + 2]);
      const y2 = parseInt(arrayPuntos[n + 3]);
      if (!isNaN(x1) && !isNaN(y1)) coordenadas.push(calcularTaladro(x1, y1));
      if (!isNaN(x2) && !isNaN(y2)) coordenadas.push(calcularTaladro(x2, y2));
      n += 4;
    }
  }

  if (props.eye.name === "oi") {
    return coordenadas.map((coord) => ({ x: -coord.x, y: coord.y }));
  }
  return coordenadas;
});

async function fetchCaracteristicasLente() {
  const b = nuevoPedido.value?.oClaseBAPI;
  const vkorg =
    b?.listCABIN?.clsZES_CAB_INPUT?.[0]?.VKORG ||
    b?.CABOUT?.VKORG ||
    b?.listCABOUT?.clsZES_CAB_OUTPUT?.[0]?.VKORG ||
    b?.VKORG ||
    "";
  const cliente = b?.CLIENTE || "";
  const lente = pedidoJergaForEye.value;
  if (!vkorg || !cliente || !lente) {
    caracteristicasLocal.value = null;
    return;
  }
  try {
    const data = await ofertasStore.GetCaracteristicasLente({
      payload: { Vkorg: vkorg, Cliente: cliente, Lente: lente },
    });
    caracteristicasLocal.value = data || null;
  } catch {
    caracteristicasLocal.value = null;
  }
}

async function refreshCaracteristicasYDraw() {
  await fetchCaracteristicasLente();
  init();
}

function calcularEscalaDinamica(valorPantalla: number) {
  // Fórmula lineal derivada de los datos:
  //  pantalla 44 mm → escala 1.35; pantalla 69 mm → escala 2.1.
  const e = 0.03 * valorPantalla + 0.03;
  return Math.round(e * 100) / 100;
}

function getPointsFromArrayPos(pos: number) {
  const adjustedAngle = Math.ceil((pos / props.numeroPuntos) * 360);
  return pointToAngle(-parseInt(String(adjustedAngle - 180)));
}

function init() {
  blankHalfWidth.value = 0;
  if (!(props.eye && (props.eye.FormaRO || props.eye.FormaSO))) return;

  calculateSvg();
  calculateCircle();
  calculateLentilla();
  computeBlankHorizontalHalfWidth();

  if (!props.eye.FormaEO) return;

  espesores.value = decodeFormas(props.eye.FormaEO, precision, props.numeroPuntos);
  const espesor = espesores.value[0];
  const espMin = Math.min(...espesores.value);
  const espMax = Math.max(...espesores.value);

  if (!props.eye.ESPESOR || props.eye.ESPESOR !== espesor) props.eye.ESPESOR = espesor;
  if (!props.eye.ESP_MIN || props.eye.ESP_MIN !== espMin) props.eye.ESP_MIN = espMin;
  if (!props.eye.ESP_MAX || props.eye.ESP_MAX !== espMax) props.eye.ESP_MAX = espMax;

  nextTick(() => {
    let pos = espesores.value.indexOf(props.eye.ESP_MAX as number);
    let pt = getPointsFromArrayPos(pos);
    if (pt !== undefined) {
      redLineX.value = pt.x;
      redLineY.value = pt.y;
    }

    pos = espesores.value.indexOf(props.eye.ESP_MIN as number);
    pt = getPointsFromArrayPos(pos);
    if (pt !== undefined) {
      greenLineX.value = pt.x;
      greenLineY.value = pt.y;
    }

    if (
      forma.value &&
      forma.value.length > 0 &&
      (!props.eye.radio || props.eye.radio !== forma.value[0])
    ) {
      props.eye.radio = forma.value[0];
    }

    montajeXi.value = (props.eye.desplazamiento || 0) + mmToPx(17) / escala.value;
    montajeXd.value = (props.eye.desplazamiento || 0) - mmToPx(17) / escala.value;
  });
}

function calculateSvg() {
  const diam = props.eye.DIA_RE || "60/60";
  const separator = diam.includes("/") ? "/" : "X";
  const split = diam.split(separator);
  const firstValue = split[0] ?? "60";

  /**
   * VSP 2024-12-16: el centro óptico se desplaza usando Cruz de montaje
   * (computed cruzMontajeX, que ya convierte mm a px). La fórmula previa
   * (diaMayor − diaMenor) / 2 no era correcta.
   */
  props.eye.desplazamiento = cruzMontajeX.value;
  diametro.value = firstValue;

  if (props.eye.name === "oi") {
    props.eye.desplazamiento = -(props.eye.desplazamiento || 0);
  }

  const REF_DIAMETRO = 75;
  props.eye.width = calculatedSize(REF_DIAMETRO);
  props.eye.height = calculatedSize(REF_DIAMETRO);

  const offset = props.eye.name === "od" ? 10 : -10;
  const xWidth = (props.eye.width || 0) / 2;
  const yHeight = (props.eye.height || 0) / 2;
  props.eye.viewBox = `${-(xWidth / 2) - offset} ${-yHeight / 2} ${xWidth} ${yHeight}`;
}

function calculateLentilla() {
  const c = caracteristicasDraw.value;
  const e = escala.value || 1.35;
  const sign = props.eye.name === "oi" ? -1 : 1;

  if (c && c.Tipo === "B") {
    if (c.TipoLentilla === null || c.TipoLentilla === "") {
      Object.assign(lentilla, { x: 0, y: 0, radio: 0 });
      return;
    }
    // Bifocal: la lentilla se centra en la cruz de montaje.
    lentilla.x = (cruzMontajeX.value / e) * sign;
    lentilla.y = cruzMontajeY.value / e;
    lentilla.radio = mmToPx(2.5) / e;
    return;
  }

  if (c && c.Tipo === "P") {
    const adicXraw = parseFloat(String(props.eye.adicX ?? 0)) || 0;
    let adicYraw = parseFloat(String(props.eye.adicY ?? 0)) || 0;

    // JH.2024-01-29: Corregir 4mm para lentes progresivo BIG NORM de Rodenstock.
    // VB: la resta se hace sobre una variable LOCAL (dblSY -= 4), NO sobre el
    // origen. Aquí antes se mutaba props.eye.adicY y, como init()/calculateLentilla
    // corre en varios watchers/eventos, restaba 4 acumulativamente en cada pasada.
    if (c.FocoMaterial === "PB") adicYraw -= 4;

    // VB: lod.Posicion = New Point(sX, Math.Abs(sY)). La adición (ADIC_POS) es
    // el punto a marcar y se dibuja siempre por debajo del CO, en la misma
    // escala (svgScale) que el contorno y la cruz para que quede alineada.
    // NO se resta 2,5 mm "hacia el CO": el port previo lo hacía y subía el
    // círculo de más.
    const adicXmm = Math.abs(adicXraw);
    const adicYmm = Math.abs(adicYraw);
    lentilla.x = mmToSvg(adicXmm) * sign;

    // VB (Forma.vb createLentilla): el dibujo depende del tipo de lentilla.
    // - FLAT/CURV2/ROUND (DibujaLentillaRedonda): el centro BAJA media anchura
    //   (adicionY + ancho/2) y el radio = ancho/2.
    // - CURV (DibujaLentillaTelegrand): en VB es un arco; aquí se aproxima como
    //   redonda (mismo desplazamiento y radio). El arco real no está portado.
    // - resto (DibujaAdicion): círculo fijo de 2,5 mm en la posición de adición.
    // El port previo solo tenía el caso fijo, por eso la bola se quedaba arriba.
    const tipoL = String(c.TipoLentilla ?? "")
      .trim()
      .toUpperCase();
    const anchoMm = parseFloat(String(c.AnchoLentilla ?? "").replace(",", ".")) || 0;
    const esRedonda = TIPOS_LENTILLA_REDONDA.has(tipoL);

    if (esRedonda && anchoMm > 0) {
      lentilla.y = mmToSvg(adicYmm + anchoMm / 2);
      lentilla.radio = mmToSvg(anchoMm / 2);
    } else {
      lentilla.y = mmToSvg(adicYmm);
      lentilla.radio = mmToSvg(2.5);
    }

    return;
  }

  Object.assign(lentilla, { x: 0, y: 0, radio: 0 });
}

/**
 * Radio del contorno (en px SVG) en la dirección de las 3 en punto (y=0), que es
 * donde arranca la línea negra del puntero. El primer punto de `calcularPuntosCircunferencia`
 * (índice 0, ángulo 0) cae exactamente en y=0, así que `forma[0]` es ese cruce.
 * Devuelve 0 si no hay forma decodificable.
 */
function contourHalfWidthAtY0(): number {
  const svgFromR0 = (arr?: number[], factor = 1): number => {
    const r0 = arr?.[0];
    return r0 == null || isNaN(r0) ? 0 : mmToSvg(r0) * factor;
  };
  let radios = forma.value;
  if (!radios?.length && props.eye.FormaRO) {
    radios = decodeFormas(props.eye.FormaRO, precision, props.numeroPuntos);
  }
  // Fall-through: si FormaRO decodifica a un array vacío, usamos el blank (FormaSO).
  if (!radios?.length && props.eye.FormaSO) {
    return svgFromR0(decodeFormas(props.eye.FormaSO, precision, props.numeroPuntos), FACTOR_DOTTED);
  }
  return svgFromR0(radios);
}

function calculateCircle() {
  const w = props.eye.width || 0;
  r.value = w / 2 - getCircleOffset.value;
  // El extremo exterior de la línea negra debe apoyarse en el CONTORNO real de la
  // lente (FormaRO en y=0), no en el semiancho del boxing (`r = width/2 - offset`),
  // que es mayor que el contorno dibujado. Con `r` la línea sobresale en el render
  // one-shot del PDF (off-screen), donde no hay re-renders que corrijan `point.x`
  // vía el efecto secundario de `pathData`. En pantalla se corregía "solo".
  const contornoX = contourHalfWidthAtY0();
  point.x = contornoX > 0 ? contornoX : r.value;
}

/**
 * Máximo |x| del contorno del blank (misma lógica que dottedLineData / calcularPuntosCircunferencia).
 * `r` (width/2 - offset) es mayor que el radio dibujado; la línea central debe usar este valor.
 */
function computeBlankHorizontalHalfWidth() {
  blankHalfWidth.value = 0;
  const formaData = isPedidoSinForma.value
    ? props.eye.FormaRO
    : props.eye.FormaSO || props.eye.FormaRO;
  if (!formaData || !ajustes.value?.VALOR_PANTALLA) return;

  const final = decodeFormas(formaData, precision, props.numeroPuntos);
  if (!final?.length) return;

  const alfaIncrease = ((-360 / props.numeroPuntos) * Math.PI) / 180;
  let d = 0;
  const scl = svgScale.value;

  let maxAbsX = 0;
  for (let i = 0; i < props.numeroPuntos; i++) {
    const ri = final[i];
    if (ri === undefined) break;
    const xv = ri * Math.cos(d) * scl * FACTOR_DOTTED;
    maxAbsX = Math.max(maxAbsX, Math.abs(xv));
    d += alfaIncrease;
  }
  blankHalfWidth.value = maxAbsX;
}

function startDrag() {
  dragging.value = true;
}

function endDrag() {
  dragging.value = false;
}

function drag(event: MouseEvent) {
  if (!dragging.value || !svg.value) return;

  const rect = svg.value.getBoundingClientRect();
  const midx = rect.left + (rect.right - rect.left) / 2;
  const midy = rect.top + (rect.bottom - rect.top) / 2;

  const angle = Math.atan2(midy - event.clientY, midx - event.clientX) * (180 / Math.PI);
  props.eye.angulo = -parseInt(String(angle - 180));

  const { x: nx, y: ny } = pointToAngle(angle);
  point.x = nx;
  point.y = ny;
}

function pointToAngle(angle: number) {
  const path = lensePath.value;
  if (!path) return { x: 0, y: 0 };

  const pathLength = path.getTotalLength();
  if (pathLength <= 0) return { x: 0, y: 0 };

  let adjustedAngle = (-angle + 360) % 360;
  adjustedAngle = (adjustedAngle * pathLength) / 360;
  adjustedAngle = (adjustedAngle + pathLength / 2) % pathLength;

  if (espesores.value.length > 0) getDataFromAngle(adjustedAngle, pathLength);

  const pointOnCircle = path.getPointAtLength(adjustedAngle);
  return { x: pointOnCircle.x || 0, y: pointOnCircle.y || 0 };
}

/**
 * Obtiene la posicion del array en base al angulo y establece el espesor y el radio.
 */
function getDataFromAngle(anguloLargo: number, total: number) {
  const pos = Math.ceil((anguloLargo * (props.numeroPuntos - 1)) / total);
  props.eye.ESPESOR = espesores.value[pos];
  props.eye.radio = forma.value[pos];
  eventBus.$emit("pointOnCircle", {
    posicion: pos,
    totalPuntos: props.numeroPuntos,
    forma: forma.value,
    ojo: props.eye,
  });
}

watch(
  () => props.eye.FormaRO,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal && newVal !== "") init();
  },
);

watch(
  () => props.eye.FormaSO,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal && newVal !== "") init();
  },
);

watch(pedidoJergaForEye, (newVal, oldVal) => {
  if (newVal === oldVal) return;
  refreshCaracteristicasYDraw();
});

function onCalcularPedidoDraw() {
  init();
}

onMounted(async () => {
  eventBus.$on("calcularPedidoDraw", onCalcularPedidoDraw);

  if (props.eye && props.eye.desplazamiento) {
    x.value = props.eye.desplazamiento;
  }

  if (props.initialAjustes?.VALOR_PANTALLA != null) {
    ajustes.value = { ...props.initialAjustes };
  } else {
    ajustes.value = await ofertasStore.LoadInfo();
  }

  if (ajustes.value && ajustes.value.VALOR_PANTALLA) {
    escala.value = calcularEscalaDinamica(ajustes.value.VALOR_PANTALLA);
  }

  await fetchCaracteristicasLente();
  init();
});

onBeforeUnmount(() => {
  eventBus.$off("calcularPedidoDraw", onCalcularPedidoDraw);
});

// `point` se expone para el pipeline de render/PDF y para tests: es el extremo
// exterior de la línea negra del puntero (y el centro del círculo arrastrable).
defineExpose({ init, refreshCaracteristicasYDraw, point });
</script>

<style lang="scss" scoped>
.horizontal-gradient {
  fill: url(#hback);
}
.text-lent {
  @apply text-xs text-center max-w-full px-1;
  overflow-wrap: anywhere;

  & > span {
    @apply text-red_darker;
  }
}
</style>
